/**
 * 确定性、规格落地的纯函数（阶段 0 已可实现的种子）。
 *
 * 这些不是训练进阶/安全判定的核心规则（那属于阶段 3），但已在规格中有明确公式，
 * 且是「重量口径」强类型的关键落点（决策 14.15 + 修正 2）。
 */

import type { WeightUnitScope } from "@jilian/shared";

/**
 * Epley 估算 1RM（§12.4）：
 *   e1RM = w * (1 + r/30)
 *
 * 约束：只用于**同一动作、同一重量口径**内部的趋势比较，
 * 不得跨器械/跨口径混用。高次数组（reps 越大）可信度越低。
 */
export function calculateEpley1RM(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    throw new Error("calculateEpley1RM: 无效重量");
  }
  if (!Number.isFinite(reps) || reps < 1) {
    throw new Error("calculateEpley1RM: 无效次数（reps >= 1）");
  }
  return weightKg * (1 + reps / 30);
}

/**
 * 重量展示（决策 14.15）。
 *
 * 哑铃使用「每只」口径，杠铃用「总重」，器械用「显示值」，辅助器械用「辅助 N kg」。
 * 数据层保存**原始口径**（weightUnitScope），展示层只按本函数渲染，
 * 不做隐蔽换算，避免误读。
 */
export function formatWeightDisplay(
  weightKg: number,
  scope: WeightUnitScope,
): string {
  switch (scope) {
    case "per_dumbbell":
      return `${weightKg} kg / 只`;
    case "per_side":
      return `${weightKg} kg / 侧`;
    case "machine_display":
      return `${weightKg} kg（器械显示值）`;
    case "bodyweight":
      return "自重";
    case "bodyweight_plus":
      return `自重 + ${weightKg} kg`;
    case "assistance":
      return `辅助 ${weightKg} kg`;
    case "total":
    default:
      return `${weightKg} kg 总重`;
  }
}

/**
 * 校验两个记录是否属于「同一趋势口径」（修正 2）。
 *
 * 只有当动作、器械版本、重量口径都一致时，重量 / 容量 / e1RM 才可比。
 * 器械更换后视为新动作变体；不同口径绝不混合成一条趋势。
 */
export function sameTrendScope(params: {
  exerciseId: string;
  exerciseVersion: number;
  weightUnitScope: WeightUnitScope;
  a: { exerciseId: string; exerciseVersion: number; weightUnitScope: WeightUnitScope };
  b: { exerciseId: string; exerciseVersion: number; weightUnitScope: WeightUnitScope };
}): boolean {
  return (
    params.a.exerciseId === params.b.exerciseId &&
    params.a.exerciseVersion === params.b.exerciseVersion &&
    params.a.weightUnitScope === params.b.weightUnitScope
  );
}

/**
 * 判断是否「辅助器械」口径（决策 14.15）。
 *
 * 辅助重量数值越大通常代表动作越容易——因此辅助器械**禁用普通 `重量 × 次数` 容量公式**，
 * 其进阶方向也需单独定义（通常在辅助减重方向上进阶）。
 */
export function isAssistanceScope(scope: WeightUnitScope): boolean {
  return scope === "assistance";
}

/**
 * 每侧加片的重量变化解释（决策 14.15）：同时解释「每侧」与「总重」。
 * 例：perSideKg=2.5 → "每侧 +2.5 kg，总重 +5 kg"。
 */
export function describePlateDelta(perSideKg: number): string {
  if (!Number.isFinite(perSideKg) || perSideKg <= 0) {
    throw new Error("describePlateDelta: 无效的每侧重量（> 0）");
  }
  const total = perSideKg * 2;
  return `每侧 +${perSideKg} kg，总重 +${total} kg`;
}

/**
 * 训练容量（修正 2）。
 *
 * 容量 = Σ(重量 × 次数)。但只有同动作、同器械、同口径下才有趋势意义；
 * 不得把「每只哑铃」「杠铃总重」「器械显示值」「辅助重量」直接相加成
 * 「精确总容量」。
 *
 * 本函数**只**在单一 `weightUnitScope` 内计算（保证不混合口径）：
 *   - 若 scope 为 `assistance`（辅助器械），**拒绝**普通容积公式（抛错）；
 *   - 哑铃等 per_dumbbell 需由调用方按产品统一规则先换算到「双侧总负荷」再传入，
 *     本函数不做隐式换算，只执行同口径内的 Σ(w × r)。
 *
 * 首版 UI 建议优先展示：已完成工作组数、各动作容量/重量/次数趋势、估算 1RM 趋势；
 * 整场总容量仅作为粗略指标并注明换算口径。
 */
export function calculateExerciseVolume(
  sets: ReadonlyArray<{ actualWeightKg: number; reps: number }>,
  weightUnitScope: WeightUnitScope,
): number {
  if (isAssistanceScope(weightUnitScope)) {
    throw new Error("calculateExerciseVolume: 辅助器械不适用普通 重量×次数 容量公式");
  }
  return sets.reduce((acc, s) => acc + s.actualWeightKg * s.reps, 0);
}
